/**
 * The pricing section's pure rules (APP-ENTITLEMENT-001 Phase 11): which anchor a site features, what the
 * three-store worked example is made of, and the words of one quote line. Every number here is the catalog's
 * (`@puffin-core/app-catalog`), every word the caller's translator's; this module types neither.
 *
 * @module @puffin-core/marketing-shell/pricing/pricingRules
 */

import {
  ANNUAL_MONTHS_CHARGED,
  DIMENSION_KEY,
  PRICING_MODEL,
  anchorEntriesOf,
  askedDimensionsOf,
  attachableEntriesOf,
  isPriced,
  minimumQuantityOf,
  publicEntryOf,
  quoteDraft,
} from '@puffin-core/app-catalog';

/**
 * The public catalog as `@puffin-core/app-catalog`'s readers take it, read off their own signature: the package's
 * generated ESM entry re-exports its values, never its `PublicCatalog` typedef, and a hand-written subset here
 * (`{apps: Object[]}`) is a second, narrower copy of the shape.
 *
 * @typedef {Parameters<typeof anchorEntriesOf>[0]} PublicCatalog
 */

/** How many of each parent-less unit the worked example counts — three stores. */
export const EXAMPLE_STORES = 3;

/** One of the example's stores runs one register beyond the three its anchor includes, so the $19.95 line shows. */
export const EXAMPLE_EXTRA_REGISTERS = 1;

/**
 * The anchor a site's pricing page features: the site's own app when it IS an anchor, else the first sellable anchor
 * the site's app attaches to, else the first sellable anchor the catalog offers.
 *
 * @param {PublicCatalog} catalog - the public catalog.
 * @param {string} siteAppKey - the app whose marketing site this is.
 * @returns {?string} an anchor key, or null when the catalog offers no sellable anchor.
 */
export function featuredAnchorKeyOf(catalog, siteAppKey) {
  const sellable = anchorEntriesOf(catalog).filter(
    (anchor) => anchor.isSellable && anchor.isOffered,
  );
  if (sellable.some((anchor) => anchor.appKey === siteAppKey)) return siteAppKey;
  const site = publicEntryOf(catalog, siteAppKey);
  const attached = sellable.find((anchor) => (site?.attachesTo || []).includes(anchor.appKey));
  if (attached) return attached.appKey;
  return sellable.length > 0 ? sellable[0].appKey : null;
}

/**
 * The quantities the worked example states for one app: every parent-less derived dimension at three, a child at
 * what the anchor includes per parent — plus one register, so the price of a register beyond the inclusion shows —
 * a declared add-on at none.
 *
 * @param {Object} entry - a public catalog row.
 * @returns {Object<string, number>}
 */
export function exampleQuantitiesOf(entry) {
  /** @type {Object<string, number>} */
  const quantities = {};
  const asked = askedDimensionsOf(entry);
  const parentsFirst = [...asked].sort((a, b) =>
    a.parentDimensionKey === null ? -1 : b.parentDimensionKey === null ? 1 : 0,
  );
  for (const dimension of parentsFirst) {
    const minimum = minimumQuantityOf(dimension, quantities);
    if (dimension.parentDimensionKey === null) {
      quantities[dimension.dimensionKey] = minimum === 0 ? 0 : minimum * EXAMPLE_STORES;
    } else {
      quantities[dimension.dimensionKey] =
        minimum + (dimension.dimensionKey === DIMENSION_KEY.REGISTER ? EXAMPLE_EXTRA_REGISTERS : 0);
    }
  }
  return quantities;
}

/**
 * The worked example's basket: the featured anchor at three stores, and the site's own app beside it when the
 * anchor offers it and its price is published.
 *
 * @param {PublicCatalog} catalog
 * @param {string} anchorAppKey
 * @param {string} siteAppKey
 * @returns {{anchorAppKey: string, appKeys: string[], quantities: Object}}
 */
export function exampleBasketOf(catalog, anchorAppKey, siteAppKey) {
  const anchor = publicEntryOf(catalog, anchorAppKey);
  const siteApp = attachableEntriesOf(catalog, anchorAppKey).find(
    (entry) => entry.appKey === siteAppKey && isPriced(entry),
  );
  const quantities = { [anchorAppKey]: exampleQuantitiesOf(anchor) };
  if (siteApp) quantities[siteAppKey] = exampleQuantitiesOf(siteApp);
  return { anchorAppKey, appKeys: siteApp ? [siteAppKey] : [], quantities };
}

/**
 * The worked example, quoted: the quote (monthly and annual figures ride on it) or the catalog's refusal by code.
 *
 * @param {PublicCatalog} catalog
 * @param {string} anchorAppKey
 * @param {string} siteAppKey
 * @returns {{basket: Object, quote: ?Object, refusal: ?Object}}
 */
export function exampleQuoteOf(catalog, anchorAppKey, siteAppKey) {
  const basket = exampleBasketOf(catalog, anchorAppKey, siteAppKey);
  return { basket, ...quoteDraft(catalog, basket) };
}

/** The months an annual term gives away: twelve less what it charges (Q-AE-26: exactly ten). */
export const ANNUAL_FREE_MONTHS = 12 - ANNUAL_MONTHS_CHARGED;

/**
 * One quote line's words: a free app, a share of recovery, or `quantity units · amount a month`.
 *
 * @param {Object} params
 * @param {Object} params.line - a `quoteBasket` line.
 * @param {Function} params.t - the `marketingShell` translator (`pricing.quoteLine`).
 * @param {Function} params.tApps - the `accountAppsUi` translator (units, "free", the share).
 * @param {(cents: number) => string} params.formatPrice
 * @param {(dimensionKey: string) => string} params.unitsKeyOf - the plural unit's key, from `accountAppsUi/copy`.
 * @returns {string}
 */
export function quoteLineText({ line, t, tApps, formatPrice, unitsKeyOf }) {
  if (line.pricingModel === PRICING_MODEL.INCLUDED_WITH_ANCHOR) return tApps('free');
  if (line.pricingModel === PRICING_MODEL.SHARE_OF_RECOVERY) {
    return tApps('shareOfRecovery', { percent: String(line.shareBps / 100) });
  }
  return t('pricing.quoteLine', {
    quantity: String(line.quantity),
    units: tApps(unitsKeyOf(line.dimensionKey)),
    amount: formatPrice(line.monthlyCents),
  });
}
